import { PRIMARY_FLOW_PAYLOAD_TIMELINE_TYPE } from 'flare-chat-core/contracts/timeline/item-types';
import { buildCommittedArtifactItems } from './committedArtifacts.js';
import { selectMessagePresentation } from '../messagePresentation.js';
import { processIdentity, processProjectionSequence } from '../processProjection.js';
import {
  extractExecutionTraceTimeline,
  resolveSettledExecutionTraceSnapshot,
} from 'flare-chat-core/contracts/trace/execution-trace-timeline';
import { isArtifactCanvasPayloadAuthoritative } from 'flare-chat-core/contracts/canvas/artifactCanvasAdmissibility';
import { messageIdentityKeyForExecutionProfile } from 'flare-chat-core/contracts/modes/execution-profile';
import {
  buildMessageAnalysisArtifactItem,
  buildMessageArtifactItem,
  buildMessagePostGenerationArtifactItem,
} from './artifactDocument.js';

const choose = (condition, whenTrue, whenFalse) => (condition ? whenTrue() : whenFalse());

const everyTruthy = (...readers) => readers.reduce((result, read) => result && read(), true);
const firstTruthy = (...readers) => readers.reduce((result, read) => result || read(), undefined);
const firstDefined = (...readers) => readers.reduce((result, read) => result ?? read(), undefined);

function resolveObject(value) {
  return (
    value
    && typeof value === 'object'
    && !Array.isArray(value)
  ) ? value : null;
}

function resolveHistoricalRoundKey(msg, authoritativeRoundId) {
  return String(firstTruthy(
    () => (msg.message_projection?.round_key),
    () => (msg.round_key),
    () => (authoritativeRoundId),
    () => (msg.run_id),
    () => (''),
  )).trim();
}

function shouldRenderHistoricalThinking(msg, executionCards = [], workflowTracker = null, trackResult = null) {
  const visibility = resolveObject(Object(resolveObject(msg.canvas_state)).timeline_visibility);
  const workflowProjection = resolveObject(msg.workflow_projection);
  const hasWorkflowProgress = Boolean(resolveObject(Object(workflowProjection).progress_projection))
    || everyTruthy(() => (Array.isArray(Object(workflowProjection).run_steps)), () => (workflowProjection.run_steps.length > 0));
  if (Object(visibility).thinking_card === false) {
    return false;
  }
  if (Object(resolveObject(msg.execution_trace)).artifact_edit) {
    return false;
  }
  if (everyTruthy(() => (!String(msg.content || '').trim()), () => (resolveObject(msg.canvas_state)))) {
    return false;
  }
  if (Object.hasOwn(msg, 'process_activity')) return Boolean(msg.process_activity?.length);
  return firstTruthy(() => (Boolean(workflowTracker)), () => (Boolean(trackResult)),
    () => (executionCards.length > 0), () => (hasWorkflowProgress), () => (String(msg.thinking_trace || '').trim()));
}

function appendUniqueMessageArtifact(items, artifactItem) {
  if (!artifactItem) {
    return;
  }
  const duplicate = items.some((item) => (
    item?.type === artifactItem.type
    && item?.artifact_id
    && item.artifact_id === artifactItem.artifact_id
  ));
  if (!duplicate) {
    items.push(artifactItem);
  }
}

function appendHistoricalThinking(items, msg, messageId, executionCards, workflowProjection, workflowTracker, trackResult, roundKey) {
  if (!shouldRenderHistoricalThinking(msg, executionCards, workflowTracker, trackResult)) {
    return;
  }
  items.push({
    id: `process:${roundKey || messageId}`,
    processIdentity: processIdentity(msg.session_id, msg.run_id, msg.exchange_id ?? msg.request_id),
    projectionSequence: processProjectionSequence(workflowProjection),
    processPhases: msg.process_activity ?? [],
    messageIdentityKey: messageIdentityKeyForExecutionProfile(msg.execution_profile ?? msg.executionProfile),
    type: 'thinking',
    agentStatus: resolveObject(msg.agent_status),
    thinkingTrace: String(firstTruthy(() => (msg.thinking_trace), () => (''))),
    executionTrace: resolveSettledExecutionTraceSnapshot(msg.execution_trace),
    executionCards,
    workflowProjection,
    workflowTracker,
    trackResult,
    roundKey,
    historical: true,
    loading: false,
  });
}

function appendHistoricalCanvas(items, historicalCanvasState, messageId, roundKey) {
  if (!everyTruthy(() => (historicalCanvasState), () => (isArtifactCanvasPayloadAuthoritative(historicalCanvasState)))) {
    return;
  }
  items.push({
    id: `canvas-state-history-${messageId}`,
    type: 'canvas_state',
    payload: historicalCanvasState,
    roundKey,
  });
  const artifactItem = buildMessageArtifactItem({
    canvasPayload: historicalCanvasState,
    id: `history-${messageId}`,
    roundKey,
    source: 'history',
  });
  if (artifactItem) {
    items.push(artifactItem);
  }
}

function appendAssistantArtifacts(items, msg, messageId, authoritativeRoundId) {
  const roundKey = resolveHistoricalRoundKey(msg, authoritativeRoundId);
  const executionCards = extractExecutionTraceTimeline(msg.execution_trace);
  const workflowProjection = resolveObject(msg.workflow_projection);
  const historicalCanvasState = resolveObject(msg.canvas_state);
  const planPayload = resolveObject(msg.plan_payload);
  const workflowTracker = resolveObject(planPayload?.workflow_tracker);
  const trackResult = resolveObject(planPayload?.track_result);
  appendHistoricalThinking(items, msg, messageId, executionCards, workflowProjection, workflowTracker, trackResult, roundKey);
  appendHistoricalCanvas(items, historicalCanvasState, messageId, roundKey);
  if (resolveObject(msg.plan_payload)) {
    const planPayload = resolveObject(msg.plan_payload);
    items.push({
      id: `primary-flow-payload-history-${messageId}`,
      type: PRIMARY_FLOW_PAYLOAD_TIMELINE_TYPE,
      payload: {
        ...planPayload,
        ...(choose(msg.run_id, () => ({ run_id: msg.run_id }), () => ({}))),
      },
      roundKey,
    });
    appendUniqueMessageArtifact(items, buildMessagePostGenerationArtifactItem({
      postGenerationResult: planPayload.post_generation_result,
      id: `history-${messageId}`,
      roundKey,
      source: 'history',
    }));
  }
  if (resolveObject(msg.workflow_projection)) {
    items.push({
      id: `workflow-projection-history-${messageId}`,
      type: 'workflow_projection',
      payload: msg.workflow_projection,
      roundKey,
    });
  }
  if (resolveObject(msg.analysis_payload)) {
    items.push({
      id: `analysis-payload-history-${messageId}`,
      type: 'analysis_payload',
      payload: msg.analysis_payload,
      roundKey,
    });
    const artifactItem = buildMessageAnalysisArtifactItem({
      analysisPayload: msg.analysis_payload,
      id: `history-${messageId}`,
      roundKey,
      source: 'history',
      messageContent: msg.content,
    });
    if (artifactItem) {
      items.push(artifactItem);
    }
  }
  if (resolveObject(msg.knowledge_search)) {
    items.push({
      id: `knowledge-search-${messageId}`,
      type: 'knowledge_search',
      payload: msg.knowledge_search,
      roundKey,
    });
  }
  if (resolveObject(msg.sourcing_candidates)) {
    items.push({
      id: `sourcing-candidates-history-${messageId}`,
      type: 'sourcing_candidates',
      payload: msg.sourcing_candidates,
      roundKey,
    });
  }
}

function appendMessageItem(items, msg, messageId, messageContent, primaryFlow, chooserState, citationPayload) {
  const authoritativeRoundId = String(firstTruthy(() => (Object(primaryFlow).round_id), () => (''))).trim();
  items.push({
    id: messageId,
    type: 'message',
    role: msg.role,
    content: messageContent,
    citationPayload,
    messageIdentityKey: messageIdentityKeyForExecutionProfile(msg.execution_profile ?? msg.executionProfile),
    message_projection: structuredClone(resolveObject(msg.message_projection)),
    createdAt: msg.created_at,
    client_request_id: firstTruthy(() => (msg.client_request_id), () => ('')),
    intake_session_id: firstTruthy(() => (msg.intake_session_id), () => ('')),
    roundKey: resolveHistoricalRoundKey(msg, authoritativeRoundId),
    roundId: authoritativeRoundId,
    roundState: String(firstTruthy(() => (Object(primaryFlow).round_state), () => (''))).trim(),
    chooserKind: String(firstTruthy(() => (Object(chooserState).kind), () => (''))).trim(),
    chooserVisible: Object(chooserState).visible === true,
    highlights: firstDefined(() => (msg.highlights), () => ([])),
    sourceTypes: firstDefined(() => (msg.sourceTypes), () => ([])),
    attachments: Array.isArray(msg.attachments) ? msg.attachments : [],
    contextUsage: resolveObject(msg.context_usage),
    contextActivity: resolveObject(msg.context_activity),
    // DOC HELPER — OBJ-09: historical replay keeps authority as read-only data.
    contextAuthority: resolveObject(msg.context_authority),
  });
}

export function appendHistoricalTimelineItems(items, messages) {
  messages.forEach((msg, index) => {
    const messageId = firstTruthy(() => (msg.message_id), () => (`msg-fallback-${index}`));
    const primaryFlow = resolveObject(msg.primary_flow);
    const chooserState = resolveObject(Object(primaryFlow).chooser_state);
    const authoritativeRoundId = String(firstTruthy(() => (Object(primaryFlow).round_id), () => (''))).trim();
    const presentation = selectMessagePresentation(msg);
    const messageContent = presentation.content;
    const shouldRenderMessageItem = presentation.visible && firstTruthy(() => (msg.role !== 'assistant'), () => (Boolean(String(messageContent || '').trim())));

    if (msg.role === 'assistant') {
      appendAssistantArtifacts(items, msg, messageId, authoritativeRoundId);
    }
    if (shouldRenderMessageItem) {
      appendMessageItem(items, msg, messageId, messageContent, primaryFlow, chooserState, presentation.citationPayload);
      if (presentation.remainingPayload) items.push({ id: `knowledge-citation-${messageId}`, type: 'knowledge_citation',
        payload: presentation.remainingPayload, roundKey: resolveHistoricalRoundKey(msg, authoritativeRoundId) });
    }
    if (msg.role === 'assistant') {
      items.push(...buildCommittedArtifactItems({ deliveredArtifacts: msg.plan_payload?.delivered_artifacts,
        conversationProjection: msg.knowledge_citation?.conversation_projection,
        roundKey: resolveHistoricalRoundKey(msg, authoritativeRoundId) }));
    }
  });
}
